/**
 * API para utilizadores do app (cadastro, login, perfil, agendamentos).
 */
import { fetchApi, handleJson, parseError } from '@/api/http'
import { resolveMediaUrl, toStorableMediaUrl } from '@/utils/mediaUrl'
import { markClienteSessionActive, setClienteBearerToken } from '@/utils/clienteAuth'
import { forceRelogin } from '@/utils/authFlow'

export interface RegistarPayload {
  nome: string
  telefone?: string
  email: string
  codigo_acesso?: string
}

export interface Usuario {
  id: number
  nome: string
  telefone: string | null
  email: string
  avatar_url?: string | null
}

export type LoginClienteResponse = Usuario & { token?: string; conta_nova?: boolean }

export type GuestCheckoutResult = { user: Usuario; contaNova: boolean }

export type UsuarioCriado = Usuario

async function fetchCliente(path: string, options?: RequestInit): Promise<Response> {
  return fetchApi(path, options)
}

async function handleClienteResponse<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão inválida ou expirada. Faça login novamente.'
      }
    })()
    forceRelogin('cliente', msg)
  }
  if (!res.ok) return parseError(res, text)
  if (!text) return undefined as T
  markClienteSessionActive()
  return JSON.parse(text) as T
}

/** Erro de auth sem hard-redirect (útil no checkout visitante). */
async function handleClienteResponseThrowAuth<T>(res: Response): Promise<T> {
  const text = await res.text()
  if (res.status === 401 || res.status === 403) {
    const msg = (() => {
      try {
        const d = JSON.parse(text) as { error?: string }
        return d?.error ?? 'Sessão inválida ou expirada. Faça login novamente.'
      } catch {
        return 'Sessão inválida ou expirada. Faça login novamente.'
      }
    })()
    throw new Error(msg)
  }
  if (!res.ok) return parseError(res, text)
  if (!text) return undefined as T
  markClienteSessionActive()
  return JSON.parse(text) as T
}

/** Não força logout — pedidos secundários (ex.: notificações no shell). */
async function handleClienteResponseSoft<T>(res: Response, fallback: T): Promise<T> {
  if (res.status === 401 || res.status === 403) return fallback
  const text = await res.text()
  if (!res.ok) return fallback
  if (!text) return fallback
  try {
    return JSON.parse(text) as T
  } catch {
    return fallback
  }
}

function mapUser(user: Usuario & { token?: string }): LoginClienteResponse {
  const mapped: LoginClienteResponse = {
    id: user.id,
    nome: user.nome,
    telefone: user.telefone ?? null,
    email: user.email,
    avatar_url: user.avatar_url ? resolveMediaUrl(user.avatar_url) : null,
  }
  if (user.token) mapped.token = user.token
  return mapped
}

function applyClienteSessionFromResponse(data: LoginClienteResponse): Usuario {
  if (data.token) setClienteBearerToken(data.token)
  markClienteSessionActive()
  return {
    id: data.id,
    nome: data.nome,
    telefone: data.telefone ?? null,
    email: data.email,
    avatar_url: data.avatar_url ?? null,
  }
}

/**
 * Login. Body: { email, codigo_acesso }. Devolve utilizador + token JWT.
 */
export async function loginUsuario(email: string, codigo_acesso: string): Promise<Usuario> {
  const res = await fetchApi('/api/usuarios/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim(), codigo_acesso: codigo_acesso.trim() }),
  })
  const data = await handleJson<LoginClienteResponse>(res)
  return applyClienteSessionFromResponse(mapUser(data))
}

export async function registarUsuario(payload: RegistarPayload): Promise<Usuario> {
  const res = await fetchApi('/api/usuarios', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      nome: payload.nome.trim(),
      telefone: payload.telefone?.trim() || null,
      email: payload.email.trim(),
      codigo_acesso: payload.codigo_acesso?.trim() || undefined,
    }),
  })
  const data = await handleJson<LoginClienteResponse>(res)
  return applyClienteSessionFromResponse(mapUser(data))
}

/** Contacto no fluxo visitante — registo silencioso; código enviado por e-mail. */
export async function guestCheckoutUsuario(payload: {
  email: string
  telefone: string
}): Promise<GuestCheckoutResult> {
  const res = await fetchApi('/api/usuarios/guest-checkout', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: payload.email.trim(),
      telefone: payload.telefone.trim(),
    }),
  })
  const data = await handleJson<LoginClienteResponse>(res)
  const contaNova = Boolean(data.conta_nova)
  return {
    user: applyClienteSessionFromResponse(mapUser(data)),
    contaNova,
  }
}

export async function solicitarRecuperacaoCodigo(email: string): Promise<{ ok: boolean; message: string }> {
  const res = await fetchApi('/api/usuarios/recuperar-codigo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.trim() }),
  })
  return handleJson(res)
}

export async function redefinirCodigoAcesso(
  token: string,
  codigoNovo: string
): Promise<Usuario> {
  const res = await fetchApi('/api/usuarios/redefinir-codigo', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, codigo_novo: codigoNovo.trim() }),
  })
  const data = await handleJson<LoginClienteResponse>(res)
  return applyClienteSessionFromResponse(mapUser(data))
}

export async function updatePerfil(
  usuarioId: number,
  data: Partial<{ nome: string; telefone: string | null; avatar_url: string | null }>
): Promise<Usuario> {
  const payload = {
    ...data,
    avatar_url:
      'avatar_url' in data
        ? data.avatar_url
          ? toStorableMediaUrl(data.avatar_url)
          : data.avatar_url
        : undefined,
  }
  const res = await fetchCliente(`/api/usuarios/${usuarioId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const user = await handleClienteResponse<Usuario>(res)
  return mapUser(user)
}

/** Eliminação permanente da conta do cliente autenticado. */
export async function deleteMyAccount(): Promise<void> {
  const res = await fetchCliente('/api/usuarios/me', { method: 'DELETE' })
  await handleClienteResponse<{ ok: boolean }>(res)
}

export interface AgendamentoResumo {
  id: string
  data: string
  hora: string
  profissional: string
  unidade: string
  status: 'agendado' | 'realizado' | 'cancelado'
  servico_id?: number | null
  servico_nome?: string | null
  google_calendar_link?: string
}

export type OcupacaoSlot = { hora: string; duracao_minutos: number }

export async function getHorariosOcupados(data: {
  data: string
  profissional: string
  unidade: string
}): Promise<{ horarios: string[]; ocupacoes: OcupacaoSlot[] }> {
  const q = new URLSearchParams({
    data: data.data,
    profissional: data.profissional,
    unidade: data.unidade,
  })
  const res = await fetchApi(`/api/agendamentos/ocupados?${q.toString()}`)
  const payload = await handleJson<{ horarios?: string[]; ocupacoes?: OcupacaoSlot[] }>(res)
  const horarios = Array.isArray(payload?.horarios) ? payload.horarios : []
  const ocupacoes = Array.isArray(payload?.ocupacoes)
    ? payload.ocupacoes.map((o) => ({
        hora: String(o.hora).slice(0, 5),
        duracao_minutos: Number(o.duracao_minutos) || 60,
      }))
    : horarios.map((h) => ({ hora: String(h).slice(0, 5), duracao_minutos: 60 }))
  return { horarios, ocupacoes }
}

export async function getDiasDisponiveisAgendamento(
  profissionalId: number,
  unidadeId: number
): Promise<{
  dias_semana: number[]
  dias_label: string[]
  datas_disponiveis: string[]
  datas_bloqueadas: string[]
  usa_padrao: boolean
  sem_horario_na_unidade: boolean
}> {
  const q = new URLSearchParams({
    profissional_id: String(profissionalId),
    unidade_id: String(unidadeId),
  })
  const res = await fetchApi(`/api/disponibilidade/dias?${q.toString()}`)
  const payload = await handleJson<{
    dias_semana?: number[]
    dias_label?: string[]
    datas_disponiveis?: string[]
    datas_bloqueadas?: string[]
    usa_padrao?: boolean
    sem_horario_na_unidade?: boolean
  }>(res)
  return {
    dias_semana: Array.isArray(payload?.dias_semana) ? payload.dias_semana : [],
    dias_label: Array.isArray(payload?.dias_label) ? payload.dias_label : [],
    datas_disponiveis: Array.isArray(payload?.datas_disponiveis) ? payload.datas_disponiveis : [],
    datas_bloqueadas: Array.isArray(payload?.datas_bloqueadas) ? payload.datas_bloqueadas : [],
    usa_padrao: !!payload?.usa_padrao,
    sem_horario_na_unidade: !!payload?.sem_horario_na_unidade,
  }
}

export async function getHorariosDisponiveis(
  profissionalId: number,
  data: string,
  unidadeId?: number,
  duracaoMinutos?: number
): Promise<{ slots: string[]; usa_padrao: boolean; sem_horario_na_unidade: boolean }> {
  const q = new URLSearchParams({
    profissional_id: String(profissionalId),
    data,
  })
  if (unidadeId != null && unidadeId > 0) q.set('unidade_id', String(unidadeId))
  if (duracaoMinutos != null && duracaoMinutos >= 5) {
    q.set('duracao_minutos', String(duracaoMinutos))
  }
  const res = await fetchApi(`/api/disponibilidade/slots?${q.toString()}`)
  const payload = await handleJson<{
    slots?: string[]
    usa_padrao?: boolean
    sem_horario_na_unidade?: boolean
  }>(res)
  return {
    slots: Array.isArray(payload?.slots) ? payload.slots : [],
    usa_padrao: !!payload?.usa_padrao,
    sem_horario_na_unidade: !!payload?.sem_horario_na_unidade,
  }
}

export async function cadastrarNewsletter(nome: string, email: string): Promise<void> {
  const res = await fetchApi('/api/newsletter', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nome: nome.trim() || null, email: email.trim() }),
  })
  await handleJson(res)
}

export interface CompraResumo {
  id: number
  usuario_id: number
  pacote_id: number
  pacote_nome: string
  data_compra: string
  metodo_pagamento: string
  status: string
  criado_em: string
}

export interface NotificacaoResumo {
  id: number
  tipo: string
  titulo: string
  mensagem: string
  agendamento_id: number | null
  lida: number
  criada_em: string
}

export interface FeedbackPublico {
  id: number
  nome: string
  texto: string
  destaque: number
  criado_em: string
}

export interface SocialPost {
  id: number
  usuario_id: number
  usuario_nome?: string
  texto: string | null
  media_url: string | null
  aprovado?: number
  ativo?: number
  destaque: number
  criado_em: string
}

export async function getAgendamentos(_usuarioId?: number): Promise<AgendamentoResumo[]> {
  const res = await fetchCliente('/api/agendamentos')
  return handleClienteResponse<AgendamentoResumo[]>(res)
}

export async function getCompras(_usuarioId?: number): Promise<CompraResumo[]> {
  const res = await fetchCliente('/api/compras')
  return handleClienteResponse<CompraResumo[]>(res)
}

export async function createCompra(data: {
  pacote_id: number
  metodo_pagamento: string
  status?: string
}): Promise<CompraResumo> {
  const res = await fetchCliente('/api/compras', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      pacote_id: data.pacote_id,
      metodo_pagamento: data.metodo_pagamento,
      status: data.status || 'pendente',
    }),
  })
  return handleClienteResponse<CompraResumo>(res)
}

export async function getNotificacoesCliente(_usuarioId?: number, limit = 20): Promise<NotificacaoResumo[]> {
  const res = await fetchCliente(`/api/notificacoes/cliente?limit=${limit}`)
  return handleClienteResponseSoft<NotificacaoResumo[]>(res, [])
}

export async function getNotificacoesNaoLidasCount(_usuarioId?: number): Promise<number> {
  const res = await fetchCliente('/api/notificacoes/cliente/unread-count')
  const data = await handleClienteResponseSoft<{ unread?: number }>(res, { unread: 0 })
  return typeof data?.unread === 'number' ? data.unread : 0
}

export async function marcarTodasNotificacoesLidas(_usuarioId?: number): Promise<number> {
  const res = await fetchCliente('/api/notificacoes/cliente/marcar-todas-lidas', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  const data = await handleClienteResponse<{ updated?: number }>(res)
  return typeof data?.updated === 'number' ? data.updated : 0
}

export async function marcarNotificacaoComoLida(
  notificacaoId: number,
  _usuarioId?: number
): Promise<NotificacaoResumo> {
  const res = await fetchCliente(`/api/notificacoes/${notificacaoId}/lida`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  })
  return handleClienteResponse<NotificacaoResumo>(res)
}

export async function getFeedbacksPublicos(): Promise<FeedbackPublico[]> {
  const res = await fetchApi('/api/feedbacks')
  return handleJson<FeedbackPublico[]>(res)
}

export async function fetchClienteSession(): Promise<Usuario | null> {
  try {
    const res = await fetchApi('/api/usuarios/session')
    if (res.status === 401 || res.status === 403) return null
    const data = await handleJson<Usuario & { ok?: boolean }>(res)
    if (!data?.id || !data.email) return null
    markClienteSessionActive()
    return mapUser(data)
  } catch {
    return null
  }
}

export async function criarFeedback(data: {
  nome?: string
  texto: string
}): Promise<FeedbackPublico> {
  const res = await fetchCliente('/api/feedbacks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return handleClienteResponse<FeedbackPublico>(res)
}

export async function getSocialPosts(): Promise<SocialPost[]> {
  const res = await fetchApi('/api/social/posts')
  const posts = await handleJson<SocialPost[]>(res)
  return posts.map((post) => ({
    ...post,
    media_url: post.media_url ? resolveMediaUrl(post.media_url) : null,
  }))
}

export async function getMySocialPosts(): Promise<SocialPost[]> {
  const res = await fetchCliente('/api/social/posts/me')
  const posts = await handleClienteResponse<SocialPost[]>(res)
  return posts.map((post) => ({
    ...post,
    media_url: post.media_url ? resolveMediaUrl(post.media_url) : null,
  }))
}

export async function createSocialPost(data: {
  texto?: string
  media_url?: string
}): Promise<SocialPost> {
  const payload = {
    ...data,
    media_url: data.media_url?.trim() ? toStorableMediaUrl(data.media_url) : undefined,
  }
  const res = await fetchCliente('/api/social/posts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const post = await handleClienteResponse<SocialPost>(res)
  return { ...post, media_url: post.media_url ? resolveMediaUrl(post.media_url) : null }
}

export async function uploadImage(file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const res = await fetchCliente('/api/upload/public', { method: 'POST', body: form })
  const data = await handleClienteResponse<{ url: string }>(res)
  return toStorableMediaUrl(data.url)
}

export async function createAgendamento(
  data: {
    data: string
    hora: string
    profissional: string
    unidade: string
    profissional_id?: number
    unidade_id?: number
    servico_id?: number | null
    status?: 'agendado' | 'realizado' | 'cancelado'
  },
  options?: { softAuth?: boolean }
): Promise<AgendamentoResumo> {
  const res = await fetchCliente('/api/agendamentos', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...data, status: data.status || 'agendado' }),
  })
  if (options?.softAuth) return handleClienteResponseThrowAuth<AgendamentoResumo>(res)
  return handleClienteResponse<AgendamentoResumo>(res)
}

export async function reagendarAgendamento(
  agendamentoId: number,
  data: { data: string; hora: string }
): Promise<AgendamentoResumo> {
  const res = await fetchCliente(`/api/agendamentos/${agendamentoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: data.data, hora: data.hora, status: 'agendado' }),
  })
  return handleClienteResponse<AgendamentoResumo>(res)
}

export async function cancelarAgendamento(agendamentoId: number): Promise<AgendamentoResumo> {
  const res = await fetchCliente(`/api/agendamentos/${agendamentoId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'cancelado' }),
  })
  return handleClienteResponse<AgendamentoResumo>(res)
}
